import { toTitleCaseString } from "@validereinc/utilities";
import classNames from "classnames/bind";
import { format } from "date-fns";
import { isPlainObject } from "es-toolkit/compat";
import React, { forwardRef, useEffect, useMemo, useState } from "react";
import {
  useController,
  type FieldValues,
  type ValidateResult,
} from "react-hook-form";
import Button from "../../Button/Button";
import { DropdownMenu } from "../../DropdownMenu/DropdownMenu";
import { Skeleton } from "../../Skeleton/Skeleton";
import { mergeProps } from "../../utils/component-props";
import { InputWithLabels } from "../_components/InputWithLabels";
import {
  DateSelectorInputBare,
  type DateSelectorProps,
  type DateValueOrRange,
} from "../DateSelectorInput";
import { DropdownInputBare } from "../DropdownInput";
import TextInputBare from "../TextInput/TextInput";
import styles from "./DynamicDataTypeInput.module.scss";
import {
  ControlledDynamicDataTypeInputProps,
  DynamicDataTypeInputDataTypes,
  DynamicDataTypeInputModes,
  DynamicDataTypeInputModesType,
  DynamicDataTypeInputProps,
  DynamicDataTypeInputSupportedDateInputFormats,
} from "./types";

const cx = classNames.bind(styles);

const InputField = ({
  dataType,
  name,
  value,
  handleChange,
  dropdownOptions,
  dateFormat,
  shouldApplyDefaultValue = false,
  shouldDisableInput = false,
  ...restProps
}: Pick<
  DynamicDataTypeInputProps,
  | "dataType"
  | "name"
  | "value"
  | "dropdownOptions"
  | "dateFormat"
  | "id"
  | "aria-invalid"
  | "aria-label"
  | "isDisabled"
> & {
  shouldApplyDefaultValue?: boolean;
  shouldDisableInput?: boolean;
  handleChange: (value: any) => void;
}) => {
  const dateFieldVariant: DateSelectorProps["variant"] = useMemo(() => {
    const mapping: Record<
      DynamicDataTypeInputSupportedDateInputFormats,
      DateSelectorProps["variant"]
    > = {
      yyyy: "year",
      yyyyMM: "month",
      "yyyy-MM-dd": "day",
    };

    if (dataType === "date" && dateFormat) {
      return mapping[dateFormat] ?? "day";
    }

    return "day";
  }, [dataType, dateFormat]);

  return (
    <>
      {(dataType === DynamicDataTypeInputDataTypes.STRING ||
        dataType === DynamicDataTypeInputDataTypes.NUMBER) && (
        <TextInputBare
          {...restProps}
          inputStyle={{
            borderRadius: "6px 0px 0px 6px",
          }}
          name={name}
          type={
            dataType === DynamicDataTypeInputDataTypes.STRING
              ? "text"
              : "number"
          }
          value={value}
          onChange={handleChange}
          isDisabled={
            shouldDisableInput ||
            shouldApplyDefaultValue ||
            restProps.isDisabled
          }
        />
      )}
      {dataType === DynamicDataTypeInputDataTypes.DATE && dateFormat && (
        <DateSelectorInputBare
          {...restProps}
          className={cx("value-input")}
          name={name}
          isRange={false}
          variant={dateFieldVariant}
          value={value}
          onChange={(newValue: DateValueOrRange | undefined) => {
            if (typeof newValue === "undefined") {
              handleChange("");
            } else {
              const formattedValue = format(
                "from" in newValue ? newValue.from : newValue,
                dateFormat
              );
              handleChange(formattedValue);
            }
          }}
          isDisabled={
            shouldDisableInput ||
            shouldApplyDefaultValue ||
            restProps.isDisabled
          }
        />
      )}
      {(dataType === DynamicDataTypeInputDataTypes.PICK_LIST ||
        dataType === DynamicDataTypeInputDataTypes.MULTI_PICK_LIST) && (
        <DropdownInputBare
          {...restProps}
          className={cx("dropdown-input")}
          name={name}
          options={dropdownOptions}
          labelKey="label"
          valueKey="value"
          value={value}
          onChange={handleChange}
          isDisabled={
            shouldDisableInput ||
            shouldApplyDefaultValue ||
            restProps.isDisabled
          }
          isMulti={dataType === DynamicDataTypeInputDataTypes.MULTI_PICK_LIST}
        />
      )}
    </>
  );
};

const DynamicDataTypeInput = forwardRef<
  HTMLDivElement,
  DynamicDataTypeInputProps
>(
  (
    {
      name,
      dataType = DynamicDataTypeInputDataTypes.STRING,
      value,
      onChange,
      defaultValue,
      variables = [],
      dropdownOptions = [],
      dateFormat,
      mode = DynamicDataTypeInputModes.CUSTOM_ANSWER,
      onModeChange,
      isBusy = false,
      isLoading = false,
      isDisabled = false,
      className,
      style,
      ...restProps
    }: DynamicDataTypeInputProps,
    ref
  ) => {
    const handleChange = (newValue: any) => {
      onChange?.(newValue);
    };

    const handleModeChange = (newMode: DynamicDataTypeInputModesType) => {
      onModeChange?.(newMode);
    };

    const actions = useMemo(
      () => [
        {
          label: toTitleCaseString(DynamicDataTypeInputModes.VARIABLE, {
            intent: "ui",
          }),
          onClick: () => {
            handleModeChange(DynamicDataTypeInputModes.VARIABLE);
          },
          isDisabled: !variables.length,
        },
        {
          label: toTitleCaseString(DynamicDataTypeInputModes.CUSTOM_ANSWER, {
            intent: "ui",
          }),
          onClick: () => {
            handleModeChange(DynamicDataTypeInputModes.CUSTOM_ANSWER);
          },
        },
        {
          label: toTitleCaseString(
            DynamicDataTypeInputModes.EXISTING_DEFAULT_ANSWER,
            {
              intent: "ui",
            }
          ),
          onClick: () => {
            handleModeChange(DynamicDataTypeInputModes.EXISTING_DEFAULT_ANSWER);
          },
          isDisabled: defaultValue === undefined,
        },
        {
          label: toTitleCaseString(DynamicDataTypeInputModes.BLANK, {
            intent: "ui",
          }),
          onClick: () => {
            handleModeChange(DynamicDataTypeInputModes.BLANK);
          },
        },
      ],
      [handleModeChange, variables, defaultValue]
    );

    useEffect(() => {
      if (mode === DynamicDataTypeInputModes.EXISTING_DEFAULT_ANSWER) {
        handleChange(defaultValue);
      } else {
        handleChange("");
      }
    }, [mode, defaultValue]);

    return (
      <div
        className={cx("container", className)}
        style={style}
        ref={ref}
      >
        <Skeleton
          className={cx("skeleton")}
          isLoading={isLoading}
          isBusy={isBusy}
        >
          <div className={cx("input-container")}>
            {mode === DynamicDataTypeInputModes.VARIABLE ? (
              <DropdownInputBare
                {...restProps}
                className={cx("dropdown-input")}
                name={name}
                options={variables ?? []}
                labelKey="label"
                valueKey="value"
                value={value}
                onChange={handleChange}
                isDisabled={isBusy || isDisabled}
              />
            ) : mode === DynamicDataTypeInputModes.CUSTOM_ANSWER ? (
              <InputField
                {...restProps}
                dataType={dataType}
                name={name}
                value={value}
                handleChange={handleChange}
                dropdownOptions={dropdownOptions}
                dateFormat={dateFormat}
                shouldApplyDefaultValue={false}
                shouldDisableInput={false}
                isDisabled={isBusy || isDisabled}
              />
            ) : mode === DynamicDataTypeInputModes.EXISTING_DEFAULT_ANSWER ? (
              <InputField
                {...restProps}
                dataType={dataType}
                name={name}
                value={value}
                handleChange={handleChange}
                dropdownOptions={dropdownOptions}
                dateFormat={dateFormat}
                shouldApplyDefaultValue
                shouldDisableInput={false}
                isDisabled={isBusy || isDisabled}
              />
            ) : mode === DynamicDataTypeInputModes.BLANK ? (
              <InputField
                {...restProps}
                dataType={dataType}
                name={name}
                value={value}
                handleChange={handleChange}
                dropdownOptions={dropdownOptions}
                dateFormat={dateFormat}
                shouldDisableInput
                isDisabled={isBusy || isDisabled}
              />
            ) : null}
          </div>
          {mode && (
            <div>
              <DropdownMenu
                options={actions}
                contentProps={{ className: cx("mode-switcher-dropdown-menu") }}
              >
                <Button
                  className={cx("mode-switcher-button")}
                  variant="outline"
                  icon="gear-six"
                  size="xx-small"
                  isLoading={isLoading}
                  isBusy={isBusy || isDisabled}
                />
              </DropdownMenu>
            </div>
          )}
        </Skeleton>
      </div>
    );
  }
);

DynamicDataTypeInput.displayName = "DynamicDataTypeInput";

export const ControlledDynamicDataTypeInput = <T extends FieldValues>({
  name,
  defaultValue,
  rules,
  label,
  isLabelShown,
  labelPosition,
  description,
  isRequired,
  isOptionalTextShown,
  shouldShowDescriptionInTooltip,
  isInline = false,
  isFluid = true,
  ...restProps
}: ControlledDynamicDataTypeInputProps<T>) => {
  const { isDisabled, isLoading, isBusy, variables = [] } = restProps;
  const { field, fieldState } = useController({
    name,
    // default value intentionally not set here
    rules: {
      ...rules,
      validate:
        rules?.validate && typeof rules.validate === "function"
          ? rules.validate
          : {
              ...(isRequired
                ? {
                    isRequired: (value: string): ValidateResult =>
                      [null, undefined, ""].includes(value)
                        ? `${typeof label === "string" ? label : "Dynamic input"} is required`
                        : undefined,
                  }
                : {}),
              ...(rules?.validate && isPlainObject(rules.validate)
                ? rules.validate
                : {}),
            },
    },
  });

  const [mode, setMode] = useState<DynamicDataTypeInputModesType>(
    variables.length
      ? DynamicDataTypeInputModes.VARIABLE
      : DynamicDataTypeInputModes.CUSTOM_ANSWER
  );

  const formattedDescription = toTitleCaseString(
    mode === DynamicDataTypeInputModes.EXISTING_DEFAULT_ANSWER
      ? "Default Answer"
      : mode,
    { intent: "ui" }
  );

  const mergedProps = useMemo(() => {
    return mergeProps(restProps, {
      onChange: field.onChange,
      onBlur: field.onBlur,
      disabled: field.disabled,
      name: field.name,
      ref: field.ref,
    });
  }, [
    restProps,
    field.onChange,
    field.onBlur,
    field.disabled,
    field.name,
    field.ref,
  ]);

  const invalidMessage = fieldState.invalid
    ? fieldState.error?.message ||
      `Input is invalid (${fieldState.error?.type})`
    : undefined;

  return (
    <InputWithLabels
      colorScheme="light"
      isDisabled={isDisabled ?? false}
      isLoading={isLoading ?? false}
      isBusy={isBusy ?? false}
      label={label}
      isLabelShown={isLabelShown}
      labelPosition={labelPosition}
      description={formattedDescription}
      isRequired={isRequired}
      isOptionalTextShown={isOptionalTextShown}
      shouldShowDescriptionInTooltip={false}
      isInline={isInline}
      isFluid={isFluid}
      invalidMessage={invalidMessage}
    >
      {({ invalidMessageId, inputId, labelId }) => (
        <DynamicDataTypeInput
          id={inputId}
          labelledById={labelId}
          error={fieldState.error}
          aria-invalid={fieldState.invalid}
          aria-describedby={invalidMessageId}
          {...(!isLabelShown && !description && typeof label === "string"
            ? { "aria-label": label }
            : {})}
          {...mergedProps}
          value={field.value}
          variables={variables}
          // default value is handled by the input itself
          defaultValue={defaultValue}
          mode={mode}
          onModeChange={setMode}
        />
      )}
    </InputWithLabels>
  );
};

ControlledDynamicDataTypeInput.displayName = "ControlledDynamicDataTypeInput";

export default DynamicDataTypeInput;
